import React from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReplyIcon from '@mui/icons-material/Reply';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  createdAt: string;
  chatbotId: string;
}

interface MessagesTableProps {
  messages: ContactMessage[];
  error: string | null;
  onUpdateStatus: (messageId: string, newStatus: 'read' | 'replied') => void;
  onDeleteMessage: (messageId: string) => void;
}

const MessagesTable: React.FC<MessagesTableProps> = ({
  messages,
  error,
  onUpdateStatus,
  onDeleteMessage
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#1a237e' }}>
        Contact Messages
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <TableContainer component={Paper} sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                    No messages found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow key={message._id}>
                  <TableCell>
                    {new Date(message.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{message.name}</TableCell>
                  <TableCell>{message.email}</TableCell>
                  <TableCell>{message.message}</TableCell>
                  <TableCell>
                    <Chip
                      label={message.status}
                      color={
                        message.status === 'new' ? 'primary' :
                        message.status === 'read' ? 'default' :
                        'success'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Mark as Read">
                        <IconButton
                          size="small"
                          onClick={() => onUpdateStatus(message._id, 'read')}
                          disabled={message.status === 'read'}
                          sx={{
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : undefined,
                            "&.Mui-disabled": {
                              color: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.3) : undefined
                            }
                          }}
                        >
                          <MarkEmailReadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mark as Replied">
                        <IconButton
                          size="small"
                          onClick={() => onUpdateStatus(message._id, 'replied')}
                          disabled={message.status === 'replied'}
                          sx={{
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : undefined,
                            "&.Mui-disabled": {
                              color: theme.palette.mode === 'dark' ? alpha(theme.palette.text.primary, 0.3) : undefined
                            }
                          }}
                        >
                          <ReplyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reply via Email">
                        <IconButton
                          size="small"
                          onClick={() => window.location.href = `mailto:${message.email}`}
                          sx={{
                            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : undefined
                          }}
                        >
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Message">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeleteMessage(message._id)}
                          sx={{
                            color: theme.palette.mode === 'dark' ? theme.palette.error.light : undefined
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MessagesTable; 